import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface AuthBackupDB extends DBSchema {
  sessions: {
    key: string;
    value: {
      key: string;
      value: string;
      timestamp: number;
    };
  };
}

class StorageBackup {
  private dbPromise: Promise<IDBPDatabase<AuthBackupDB>> | null = null;
  private memoryBackup: Map<string, string> = new Map();
  private dbInitializing = false;

  private async getDB(): Promise<IDBPDatabase<AuthBackupDB> | null> {
    // If already initializing, wait for it
    if (this.dbInitializing && this.dbPromise) {
      try {
        return await this.dbPromise;
      } catch {
        // Failed, will retry below
      }
    }

    // If we have a valid connection, use it
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        // Test if connection is still valid
        await db.get('sessions', 'test');
        return db;
      } catch {
        // Connection is dead, reset
        this.dbPromise = null;
      }
    }

    // Create new connection
    if (!this.dbInitializing) {
      this.dbInitializing = true;
      this.dbPromise = openDB<AuthBackupDB>('auth-backup', 1, {
        upgrade(db: IDBPDatabase<AuthBackupDB>) {
          if (!db.objectStoreNames.contains('sessions')) {
            db.createObjectStore('sessions', { keyPath: 'key' });
          }
        },
      }).then(db => {
        this.dbInitializing = false;
        return db;
      }).catch((err: Error) => {
        console.error('Failed to open IndexedDB:', err);
        this.dbInitializing = false;
        this.dbPromise = null;
        return null as any;
      });
    }

    try {
      return await this.dbPromise;
    } catch {
      return null;
    }
  }

  async backup(key: string, value: string): Promise<void> {
    // Level 1: Memory backup (survives storage clearing, not page refresh)
    this.memoryBackup.set(key, value);
    console.log(`💾 Backed up ${key} to memory`);

    // Level 2: Try sessionStorage
    try {
      sessionStorage.setItem(key, value);
      console.log(`💾 Backed up ${key} to sessionStorage`);
    } catch (e) {
      console.warn('SessionStorage backup failed:', e);
    }

    // Level 3: IndexedDB (survives almost everything)
    try {
      const db = await this.getDB();
      if (db) {
        await db.put('sessions', {
          key,
          value,
          timestamp: Date.now()
        });
        console.log(`💾 Backed up ${key} to IndexedDB`);
      }
    } catch (e) {
      console.warn('IndexedDB backup failed:', e);
    }
  }

  async restore(key: string): Promise<string | null> {
    // Try memory first (fastest)
    const memoryValue = this.memoryBackup.get(key);
    if (memoryValue) {
      console.log(`✅ Restored ${key} from memory backup`);
      return memoryValue;
    }

    // Try sessionStorage
    try {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue) {
        console.log(`✅ Restored ${key} from sessionStorage backup`);
        this.memoryBackup.set(key, sessionValue); // Re-populate memory
        return sessionValue;
      }
    } catch (e) {
      console.warn('SessionStorage restore failed:', e);
    }

    // Try IndexedDB
    try {
      const db = await this.getDB();
      if (db) {
        const record = await db.get('sessions', key);
        if (record && record.value) {
          console.log(`✅ Restored ${key} from IndexedDB backup`);
          this.memoryBackup.set(key, record.value); // Re-populate memory
          
          // Also restore to sessionStorage if possible
          try {
            sessionStorage.setItem(key, record.value);
          } catch {}
          
          return record.value;
        }
      }
    } catch (e) {
      console.warn('IndexedDB restore failed:', e);
    }

    return null;
  }

  // Clean up old entries (older than 7 days)
  async cleanup(): Promise<void> {
    try {
      const db = await this.getDB();
      if (db) {
        const tx = db.transaction('sessions', 'readwrite');
        const store = tx.objectStore('sessions');
        const allKeys = await store.getAllKeys();
        
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        
        for (const key of allKeys) {
          const record = await store.get(key);
          if (record && record.timestamp < sevenDaysAgo) {
            await store.delete(key);
          }
        }
      }
    } catch (e) {
      console.warn('Cleanup failed:', e);
    }
  }
}

// Export singleton instance
export const storageBackup = new StorageBackup(); 